import {useState, useEffect} from "react";

export const LiveServicesTechRequestForm = ({ onUpdate, active, isViewOnly = false, initialData }) => {

    const [formData, setFormData] = useState({
        handheldMicsRequired: false,
        handheldMicCount: '',
        lapelMicsRequired: false,
        lapelMicCount: '',
        choirMicsRequired: false,
        choirMicCount: '',
        backWallLights: false,
        frontOnLights: false,
        sideOnLights: false,
        tracksCD: false,
        tracksiPhone: false,
        tracksiPad: false,
        keyboardRequired: false,
        drumsRequired: false,
        otherInstrumentRequired: false,
        otherInstrument: '',
        songLyricsProjection: false,
        lyricVideosRequired: false,
        powerpointPresentation: false,
        videoRequired: false,
        videoDetails: '',
        livestreamingRequired: false,
        videoRecordingRequired: false,
        techPersonnelRequired: false,
        technicalNotes: ''
    });

    // Populate form with initial data when provided
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setFormData(prevState => ({
                ...prevState,
                ...initialData
            }));
        }
    }, [initialData]);

    const onChange = (event) => {
        const { name, type, checked, value } = event.target;
        const updatedValue = type === 'checkbox' ? checked : value;
        const newData = { ...formData, [name]: updatedValue };
        setFormData(newData);
        onUpdate(newData); // Pass updated data to parent
    };

    if (!active) return null; // Only render if active

    return (
        <div className="rounded-lg">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-6">
                    <p className="text-sm text-gray-500 mb-4">
                        Select all technical equipment and services required for your event. For items with quantities, specify the number needed.
                    </p>
                </div>
                
                {/* Audio Section */}
                <div className="sm:col-span-6 border-t border-gray-200 pt-4 mb-2">
                    <h4 className="text-sm font-medium text-indigo-800">Audio Equipment</h4>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="handheldMicsRequired"
                                id="handheldMicsRequired"
                                checked={formData.handheldMicsRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="handheldMicsRequired" className="font-medium text-gray-700">Handheld Microphones</label>
                        </div>
                    </div>
                    
                    {formData.handheldMicsRequired && (
                        <div className="mt-2 ml-7">
                            <input
                                type="number"
                                name="handheldMicCount"
                                id="handheldMicCount"
                                min="1"
                                max="8"
                                value={formData.handheldMicCount}
                                onChange={onChange}
                                disabled={isViewOnly}
                                readOnly={isViewOnly}
                                className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                placeholder="Quantity (max 8)"
                            />
                        </div>
                    )}
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="lapelMicsRequired"
                                id="lapelMicsRequired"
                                checked={formData.lapelMicsRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="lapelMicsRequired" className="font-medium text-gray-700">Lapel/Headset Mics</label>
                        </div>
                    </div>
                    
                    {formData.lapelMicsRequired && (
                        <div className="mt-2 ml-7">
                            <input
                                type="number"
                                name="lapelMicCount"
                                id="lapelMicCount"
                                min="1"
                                max="8"
                                value={formData.lapelMicCount}
                                onChange={onChange}
                                disabled={isViewOnly}
                                readOnly={isViewOnly}
                                className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                placeholder="Quantity (max 8)"
                            />
                        </div>
                    )}
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="choirMicsRequired"
                                id="choirMicsRequired"
                                checked={formData.choirMicsRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="choirMicsRequired" className="font-medium text-gray-700">Choir Microphones</label>
                        </div>
                    </div>
                    
                    {formData.choirMicsRequired && (
                        <div className="mt-2 ml-7">
                            <input
                                type="number"
                                name="choirMicCount"
                                id="choirMicCount"
                                min="1"
                                max="4"
                                value={formData.choirMicCount}
                                onChange={onChange}
                                disabled={isViewOnly}
                                readOnly={isViewOnly}
                                className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                placeholder="Quantity (max 4)"
                            />
                        </div>
                    )}
                </div>
                
                {/* Lighting Section */}
                <div className="sm:col-span-6 border-t border-gray-200 pt-4 mb-2">
                    <h4 className="text-sm font-medium text-indigo-800">Stage Lighting</h4>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="backWallLights"
                                id="backWallLights"
                                checked={formData.backWallLights}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="backWallLights" className="font-medium text-gray-700">Back Wall Lighting</label>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="frontOnLights"
                                id="frontOnLights"
                                checked={formData.frontOnLights}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="frontOnLights" className="font-medium text-gray-700">Front Lighting</label>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="sideOnLights"
                                id="sideOnLights"
                                checked={formData.sideOnLights}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="sideOnLights" className="font-medium text-gray-700">Side Lighting</label>
                        </div>
                    </div>
                </div>
                
                {/* Music Section */}
                <div className="sm:col-span-6 border-t border-gray-200 pt-4 mb-2">
                    <h4 className="text-sm font-medium text-indigo-800">Music & Instruments</h4>
                </div>
                
                <div className="sm:col-span-6">
                    <label className="text-sm font-medium text-gray-700">Accompaniment Tracks:</label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-y-2 gap-x-4">
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    type="checkbox"
                                    name="tracksCD"
                                    id="tracksCD"
                                    checked={formData.tracksCD}
                                    onChange={onChange}
                                    disabled={isViewOnly}
                                    className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label htmlFor="tracksCD" className="font-medium text-gray-700">CD</label>
                            </div>
                        </div>
                        
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    type="checkbox"
                                    name="tracksiPhone"
                                    id="tracksiPhone"
                                    checked={formData.tracksiPhone}
                                    onChange={onChange}
                                    disabled={isViewOnly}
                                    className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label htmlFor="tracksiPhone" className="font-medium text-gray-700">iPhone</label>
                            </div>
                        </div>
                        
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    type="checkbox"
                                    name="tracksiPad"
                                    id="tracksiPad"
                                    checked={formData.tracksiPad}
                                    onChange={onChange}
                                    disabled={isViewOnly}
                                    className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label htmlFor="tracksiPad" className="font-medium text-gray-700">iPad</label>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="keyboardRequired"
                                id="keyboardRequired"
                                checked={formData.keyboardRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="keyboardRequired" className="font-medium text-gray-700">Keyboard</label>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="drumsRequired"
                                id="drumsRequired"
                                checked={formData.drumsRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="drumsRequired" className="font-medium text-gray-700">Drum Set</label>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="otherInstrumentRequired"
                                id="otherInstrumentRequired"
                                checked={formData.otherInstrumentRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="otherInstrumentRequired" className="font-medium text-gray-700">Other Instrument</label>
                        </div>
                    </div>
                    
                    {formData.otherInstrumentRequired && (
                        <div className="mt-2 ml-7">
                            <input
                                type="text"
                                name="otherInstrument"
                                id="otherInstrument"
                                value={formData.otherInstrument}
                                onChange={onChange}
                                disabled={isViewOnly}
                                readOnly={isViewOnly}
                                className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                placeholder="Specify instrument"
                            />
                        </div>
                    )}
                </div>
                
                {/* Projection & Video Section */}
                <div className="sm:col-span-6 border-t border-gray-200 pt-4 mb-2">
                    <h4 className="text-sm font-medium text-indigo-800">Visuals & Projection</h4>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="songLyricsProjection"
                                id="songLyricsProjection"
                                checked={formData.songLyricsProjection}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="songLyricsProjection" className="font-medium text-gray-700">Song Lyrics Projection</label>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="lyricVideosRequired"
                                id="lyricVideosRequired"
                                checked={formData.lyricVideosRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="lyricVideosRequired" className="font-medium text-gray-700">Lyric Videos</label>
                            <p className="text-gray-500 text-xs">Contact IT for song choices</p>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="powerpointPresentation"
                                id="powerpointPresentation"
                                checked={formData.powerpointPresentation}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="powerpointPresentation" className="font-medium text-gray-700">PowerPoint with Clicker</label>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-6">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="videoRequired"
                                id="videoRequired"
                                checked={formData.videoRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="videoRequired" className="font-medium text-gray-700">Video Playback</label>
                        </div>
                    </div>
                    
                    {formData.videoRequired && (
                        <div className="mt-2 ml-7">
                            <input
                                type="text"
                                name="videoDetails"
                                id="videoDetails"
                                value={formData.videoDetails}
                                onChange={onChange}
                                disabled={isViewOnly}
                                readOnly={isViewOnly}
                                className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                                placeholder="Video format and details"
                            />
                        </div>
                    )}
                </div>
                
                {/* Production Services */}
                <div className="sm:col-span-6 border-t border-gray-200 pt-4 mb-2">
                    <h4 className="text-sm font-medium text-indigo-800">Production Services</h4>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="livestreamingRequired"
                                id="livestreamingRequired"
                                checked={formData.livestreamingRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="livestreamingRequired" className="font-medium text-gray-700">Livestreaming</label>
                            <p className="text-gray-500 text-xs">Service will be streamed live</p>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="videoRecordingRequired"
                                id="videoRecordingRequired"
                                checked={formData.videoRecordingRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="videoRecordingRequired" className="font-medium text-gray-700">Video Recording</label>
                            <p className="text-gray-500 text-xs">Event will be recorded</p>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-2">
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                name="techPersonnelRequired"
                                id="techPersonnelRequired"
                                checked={formData.techPersonnelRequired}
                                onChange={onChange}
                                disabled={isViewOnly}
                                className={`${!isViewOnly ? 'focus:ring-indigo-500' : ''} h-4 w-4 ${isViewOnly ? 'bg-gray-100' : 'text-indigo-600'} border-gray-300 rounded`}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="techPersonnelRequired" className="font-medium text-gray-700">Tech Personnel</label>
                            <p className="text-gray-500 text-xs">Tech staff needed for event</p>
                        </div>
                    </div>
                </div>
                
                <div className="sm:col-span-6">
                    <label htmlFor="technicalNotes" className="block text-sm font-medium text-gray-700">Additional Technical Notes</label>
                    <div className="mt-1">
                        <textarea
                            name="technicalNotes"
                            id="technicalNotes"
                            rows="3"
                            value={formData.technicalNotes}
                            onChange={onChange}
                            disabled={isViewOnly}
                            readOnly={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Any other technical requirements or special instructions"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}